import React, { useMemo, useState, useCallback, useEffect } from "react";
import { makeStyles } from "@material-ui/styles";
import { useTracker } from "@dpdgroupuk/react-event-tracker";
import { isNil } from "ramda";

import RightDrawerModal from "../../RightDrawerModal";
import {
  NEW_SPLIT,
  APPLY_SPLIT,
  SELECTED_PARCELS,
  RESULT,
} from "../../../constants/strings";
import palette from "../../styles/palette";
import Button from "../../Button";
import ModalTable from "../../common/Table/ModalTable";
import {
  splitStopColumnsConfigs,
  mergeStopsColumnsConfigs,
} from "../../../enums/columnsConfigs";
import {
  headerCellModalStyles,
  bodyCellModalStyles,
} from "../../styles/tablesCells";
import parcelDetailsColumns from "../../RouteManagement/ParcelDetails/parcelDetailsColumns";
import RADIO_STOPS from "../../../enums/radioStops";
import SplitInto from "./SplitInto";
import stopListColumns from "../../RouteDetail/StopList/stopListColumns";
import {
  getMergeSplitTableData,
  prepareTableData,
  createStopOptions,
  getDropdownListSplitStop,
} from "../../../models/editStop";
import { SPLIT_STOP_MODAL } from "../../../constants/analytics";

const useStyles = makeStyles({
  button: {
    border: palette.btn.blueBorder,
  },
});

const SplitStop = ({
  open,
  onClose,
  onConfirm,
  onApplySplit,
  dropdownListStops,
  selectedParcels,
  fetchDestinationStop,
  hideSplitStopNotifications,
}) => {
  const classes = useStyles();
  const tracker = useTracker();
  const headerCellClasses = headerCellModalStyles();
  const bodyCellClasses = bodyCellModalStyles();
  const [selectedStopType, setSelectedStopType] = useState(
    RADIO_STOPS.NEW_STOP
  );
  const [shouldMatchPostcodes, toggleShouldMatchPostcodes] = useState(true);
  const [selectedStop, setSelectedStop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [destinationStop, setDestinationStop] = useState(null);
  const isNewStopType = selectedStopType === RADIO_STOPS.NEW_STOP;

  const fetchStop = useCallback(
    async (selectedStopNumber) => {
      if (!isNil(selectedStopNumber)) {
        tracker.logEvent({
          actionId: SPLIT_STOP_MODAL.SELECT_SPLIT_INTO_STOP,
          value: selectedStopNumber,
        });
      }
      setSelectedStop(selectedStopNumber);
      setLoading(true);
      const stop = await fetchDestinationStop(selectedStopNumber);
      setDestinationStop(stop);
      setLoading(false);
    },
    [fetchDestinationStop, tracker]
  );

  const dropdownStopsOptions = useMemo(
    () =>
      createStopOptions(
        getDropdownListSplitStop(
          selectedParcels,
          dropdownListStops,
          shouldMatchPostcodes
        )
      ),
    [dropdownListStops, selectedParcels, shouldMatchPostcodes]
  );

  const topTableParcelsData = useMemo(() => prepareTableData(selectedParcels), [
    selectedParcels,
  ]);

  const onChangeShouldMatchPostcodes = useCallback(
    (e, value) => {
      tracker.logEvent({
        actionId: SPLIT_STOP_MODAL.SELECT_MATCHING_POSTCODES,
        value,
      });
      toggleShouldMatchPostcodes(value);

      if (!value) {
        setSelectedStop(null);
        setDestinationStop(null);
      }
    },
    [tracker]
  );

  const handleChangeSelectedStopType = useCallback(
    (e) => {
      const type = e.target.value;
      const actionId =
        type === RADIO_STOPS.NEW_STOP
          ? SPLIT_STOP_MODAL.SELECT_NEW_STOP
          : SPLIT_STOP_MODAL.SELECT_EXISTING_STOP;

      tracker.logEvent(actionId);
      setSelectedStopType(type);
    },
    [tracker]
  );

  const [topTableColumns, bottomTableColumns] = useMemo(
    () => [
      parcelDetailsColumns({
        headerCellClasses,
        bodyCellClasses,
        columnsConfigs: splitStopColumnsConfigs,
      }),
      stopListColumns({
        headerCellClasses,
        bodyCellClasses,
        columnsConfigs: mergeStopsColumnsConfigs,
      }),
    ],
    [bodyCellClasses, headerCellClasses]
  );

  const bottomTableStopsData = useMemo(
    () => destinationStop && getMergeSplitTableData([destinationStop]),
    [destinationStop]
  );

  const handleApplySplitStop = useCallback(async () => {
    tracker.logEvent(SPLIT_STOP_MODAL.CLICK_APPLY_SPLIT);
    setLoading(true);
    try {
      (await onApplySplit(destinationStop, isNewStopType)) && onConfirm();
    } finally {
      setLoading(false);
    }
  }, [destinationStop, isNewStopType, onApplySplit, onConfirm, tracker]);

  const handleClose = useCallback(() => {
    tracker.logEvent(SPLIT_STOP_MODAL.CLICK_CLOSE);
    hideSplitStopNotifications();
    onClose();
  }, [onClose, hideSplitStopNotifications, tracker]);

  useEffect(() => {
    if (isNewStopType) {
      fetchStop(null);
    } else {
      setDestinationStop(null);
    }
  }, [fetchStop, isNewStopType]);

  return (
    <RightDrawerModal
      open={open}
      onClose={handleClose}
      title={NEW_SPLIT.toUpperCase()}
      loadId={SPLIT_STOP_MODAL.LOAD}
      actions={
        <Button
          data-test-id="apply split"
          variant="contained"
          disabled={!destinationStop || loading}
          bgColor={palette.modalButtons.blue}
          onClick={handleApplySplitStop}
          className={classes.button}
          opacityDisabled={0.38}
        >
          {APPLY_SPLIT}
        </Button>
      }
    >
      <ModalTable
        title={SELECTED_PARCELS}
        columns={topTableColumns}
        data={topTableParcelsData}
      />
      <SplitInto
        handleChangeSelectedStopType={handleChangeSelectedStopType}
        isNewStopType={isNewStopType}
        onChangeShouldMatchPostcodes={onChangeShouldMatchPostcodes}
        shouldMatchPostcodes={shouldMatchPostcodes}
        selectedStop={selectedStop}
        setSelectedStop={fetchStop}
        dropdownStopsOptions={dropdownStopsOptions}
        disabled={loading}
      />
      {destinationStop && (
        <ModalTable
          title={RESULT}
          columns={bottomTableColumns}
          data={bottomTableStopsData}
        />
      )}
    </RightDrawerModal>
  );
};

export default React.memo(SplitStop);
